import type { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';

import { loginRequest } from './msalConfig';

interface AuthData {
  accessToken: string;
  user?: AccountInfo;
  userId: string;
}

export function useAuth() {
  const { accounts, instance } = useMsal();

  const getJWT = useCallback(async (): Promise<AuthData | null> => {
    if (accounts.length === 0) {
      return null;
    }

    const account = accounts[0];

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });

      return {
        accessToken: response.accessToken,
        user: account,
        userId: account.localAccountId
      };
    } catch {
      console.error('[useAuth] Error getting access token');
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        return {
          accessToken: response.accessToken,
          user: response.account,
          userId: response.account.localAccountId
        };
      } catch {
        console.error('[useAuth] Error acquiring token via popup');
        return null;
      }
    }
  }, [accounts, instance]);

  const signOut = () => {
    void instance.logoutRedirect({
      postLogoutRedirectUri:
        import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin
    });
  };

  return {
    accounts,
    getJWT,
    isAuthenticated: accounts.length > 0,
    signOut,
    user: accounts[0]
  };
}
